import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
    FaArrowLeft,
    FaPlus,
    FaTrash,
    FaToggleOn,
    FaToggleOff,
    FaTag,
    FaPercent,
    FaMoneyBillWave,
    FaCopy,
    FaCheck,
    FaCalendarAlt,
    FaUsers,
    FaShoppingCart
} from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import firebaseService from '../../services/firebaseService';
import { useCustomAlert } from '../../hooks/useCustomAlert';
import CustomAlert from '../CustomAlert/CustomAlert';
import { useLoadingContext } from '../Loading/LoadingProvider';
import Loading from '../Loading/Loading';
import './AdminOrders.css';
import './PromoCodeManager.css';

function PromoCodeManager() {
    const navigate = useNavigate();
    const { alertState, showAlert } = useCustomAlert();
    const { startComponentLoading, stopComponentLoading, isComponentLoading } = useLoadingContext();
    const [promoCodes, setPromoCodes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [copiedCode, setCopiedCode] = useState(null);
    const [formData, setFormData] = useState({
        code: '',
        discountType: 'percentage',
        discountValue: '',
        maxUses: '',
        minOrderAmount: '',
        expiryDate: ''
    });
    const [formError, setFormError] = useState('');

    // Check if admin is logged in
    useEffect(() => {
        const isLoggedIn = localStorage.getItem('adminLoggedIn');
        if (!isLoggedIn) {
            navigate('/admin/login');
            return;
        }
    }, [navigate]);

    // Load promo codes
    useEffect(() => {
        loadPromoCodes();
    }, []);

    const loadPromoCodes = async () => {
        try {
            setLoading(true);
            const result = await firebaseService.getPromoCodes();
            if (result.success) {
                setPromoCodes(result.promoCodes);
            }
        } catch (error) {
            console.error('Error loading promo codes:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        if (name === 'code') {
            setFormData(prev => ({ ...prev, [name]: value.toUpperCase().replace(/[^A-Z0-9]/g, '') }));
        } else if (name === 'discountValue' || name === 'maxUses' || name === 'minOrderAmount') {
            setFormData(prev => ({ ...prev, [name]: value.replace(/[^0-9.]/g, '') }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
        setFormError('');
    };

    const generateRandomCode = () => {
        const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
        let code = '';
        for (let i = 0; i < 8; i++) {
            code += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        setFormData(prev => ({ ...prev, code }));
    };

    const handleCreatePromo = async (e) => {
        e.preventDefault();

        if (!formData.code.trim()) {
            setFormError('يرجى إدخال كود الخصم');
            return;
        }
        if (!formData.discountValue || Number(formData.discountValue) <= 0) {
            setFormError('يرجى إدخال قيمة الخصم');
            return;
        }
        if (formData.discountType === 'percentage' && Number(formData.discountValue) > 100) {
            setFormError('نسبة الخصم لا يمكن أن تتجاوز 100%');
            return;
        }

        const result = await firebaseService.createPromoCode(formData);
        if (result.success) {
            setFormData({
                code: '',
                discountType: 'percentage',
                discountValue: '',
                maxUses: '',
                minOrderAmount: '',
                expiryDate: ''
            });
            setShowCreateForm(false);
            loadPromoCodes();
        } else {
            setFormError(result.error);
        }
    };

    const handleDeletePromo = (promoId, promoCode) => {
        showAlert(
            `هل أنت متأكد من حذف كود الخصم "${promoCode}"؟ هذا الإجراء لا يمكن التراجع عنه.`,
            'delete',
            async () => {
                try {
                    startComponentLoading('deletePromo', 'Deleting promo code...');
                    const result = await firebaseService.deletePromoCode(promoId);
                    if (result.success) {
                        loadPromoCodes();
                    }
                } finally {
                    stopComponentLoading('deletePromo');
                }
            },
            () => { },
            'حذف الكود',
            'إلغاء'
        );
    };

    const handleTogglePromo = async (promoId, currentActive) => {
        const result = await firebaseService.togglePromoCode(promoId, !currentActive);
        if (result.success) {
            setPromoCodes(prev => prev.map(p =>
                p.id === promoId ? { ...p, isActive: !currentActive } : p
            ));
        }
    };

    const copyCode = (code) => {
        navigator.clipboard.writeText(code);
        setCopiedCode(code);
        setTimeout(() => setCopiedCode(null), 2000);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'No Expiry';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const isExpired = (expiryDate) => {
        if (!expiryDate) return false;
        return new Date(expiryDate) < new Date();
    };

    if (loading) {
        return (
            <div className="admin-orders-container">
                <div className="admin-loading-state">
                    <div className="admin-loading-spinner">
                        <div className="spinner-ring"></div>
                        <div className="spinner-ring"></div>
                        <div className="spinner-ring"></div>
                    </div>
                    <p className="admin-loading-text">Loading promo codes...</p>
                </div>
            </div>
        );
    }

    return (
        <motion.div
            className="admin-orders-container"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
        >
            {/* Header */}
            <div className="admin-header">
                <div className="header-top">
                    <Link to="/admin/orders" className="back-to-home-btn">
                        <FaArrowLeft />
                        Back to Orders
                    </Link>
                    <h1>🎫 Promo Code Manager</h1>
                </div>

                <div className="admin-stats">
                    <div className="stat-card">
                        <FaTag />
                        <div>
                            <span className="stat-number">{promoCodes.length}</span>
                            <span className="stat-label">Total Codes</span>
                        </div>
                    </div>
                    <div className="stat-card">
                        <FaToggleOn />
                        <div>
                            <span className="stat-number">{promoCodes.filter(p => p.isActive).length}</span>
                            <span className="stat-label">Active Codes</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Create New Button */}
            <div className="promo-actions">
                <button
                    className="create-promo-btn"
                    onClick={() => setShowCreateForm(!showCreateForm)}
                >
                    <FaPlus />
                    {showCreateForm ? 'Cancel' : 'Create New Promo Code'}
                </button>
            </div>

            {/* Create Form */}
            {showCreateForm && (
                <motion.div
                    className="promo-create-form"
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                >
                    <h3>Create New Promo Code</h3>
                    <form onSubmit={handleCreatePromo}>
                        <div className="promo-form-grid">
                            <div className="promo-form-group">
                                <label>Promo Code *</label>
                                <div className="code-input-wrapper">
                                    <input
                                        type="text"
                                        name="code"
                                        value={formData.code}
                                        onChange={handleInputChange}
                                        placeholder="e.g. SUMMER25"
                                        maxLength={15}
                                    />
                                    <button type="button" className="generate-btn" onClick={generateRandomCode}>
                                        Generate
                                    </button>
                                </div>
                            </div>

                            <div className="promo-form-group">
                                <label>Discount Type *</label>
                                <select
                                    name="discountType"
                                    value={formData.discountType}
                                    onChange={handleInputChange}
                                >
                                    <option value="percentage">Percentage (%)</option>
                                    <option value="fixed">Fixed Amount (EGP)</option>
                                </select>
                            </div>

                            <div className="promo-form-group">
                                <label>Discount Value *</label>
                                <div className="value-input-wrapper">
                                    <input
                                        type="text"
                                        name="discountValue"
                                        value={formData.discountValue}
                                        onChange={handleInputChange}
                                        placeholder={formData.discountType === 'percentage' ? 'e.g. 25' : 'e.g. 50'}
                                    />
                                    <span className="value-suffix">
                                        {formData.discountType === 'percentage' ? '%' : 'EGP'}
                                    </span>
                                </div>
                            </div>

                            <div className="promo-form-group">
                                <label>Max Uses (empty = unlimited)</label>
                                <input
                                    type="text"
                                    name="maxUses"
                                    value={formData.maxUses}
                                    onChange={handleInputChange}
                                    placeholder="Unlimited"
                                />
                            </div>

                            <div className="promo-form-group">
                                <label>Min Order Amount (EGP)</label>
                                <input
                                    type="text"
                                    name="minOrderAmount"
                                    value={formData.minOrderAmount}
                                    onChange={handleInputChange}
                                    placeholder="No minimum"
                                />
                            </div>

                            <div className="promo-form-group">
                                <label>Expiry Date (empty = no expiry)</label>
                                <input
                                    type="date"
                                    name="expiryDate"
                                    value={formData.expiryDate}
                                    onChange={handleInputChange}
                                    min={new Date().toISOString().split('T')[0]}
                                />
                            </div>
                        </div>

                        {formError && <p className="promo-form-error">{formError}</p>}

                        <button type="submit" className="submit-promo-btn">
                            <FaPlus /> Create Promo Code
                        </button>
                    </form>
                </motion.div>
            )}

            {/* Promo Codes List */}
            <div className="promo-codes-list">
                {promoCodes.length === 0 ? (
                    <div className="no-orders">
                        <FaTag />
                        <h3>No Promo Codes Yet</h3>
                        <p>Create your first promo code to offer discounts to customers.</p>
                    </div>
                ) : (
                    <AnimatePresence>
                        {promoCodes.map((promo) => (
                            <motion.div
                                key={promo.id}
                                className={`promo-card ${!promo.isActive ? 'inactive' : ''} ${isExpired(promo.expiryDate) ? 'expired' : ''}`}
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.9, y: 20 }}
                                transition={{ duration: 0.2 }}
                            >
                                <div className="promo-card-header">
                                    <div className="promo-code-display">
                                        <span className="promo-code-text">{promo.code}</span>
                                        <button
                                            className="copy-code-btn"
                                            onClick={() => copyCode(promo.code)}
                                            title="Copy code"
                                        >
                                            {copiedCode === promo.code ? <FaCheck /> : <FaCopy />}
                                        </button>
                                    </div>
                                    <div className="promo-status-badges">
                                        {!promo.isActive && <span className="promo-badge badge-inactive">Inactive</span>}
                                        {isExpired(promo.expiryDate) && <span className="promo-badge badge-expired">Expired</span>}
                                        {promo.isActive && !isExpired(promo.expiryDate) && <span className="promo-badge badge-active">Active</span>}
                                    </div>
                                </div>

                                <div className="promo-card-body">
                                    <div className="promo-detail">
                                        {promo.discountType === 'percentage' ? <FaPercent /> : <FaMoneyBillWave />}
                                        <span>
                                            {promo.discountType === 'percentage'
                                                ? `${promo.discountValue}% off`
                                                : `${promo.discountValue} EGP off`}
                                        </span>
                                    </div>

                                    <div className="promo-detail">
                                        <FaUsers />
                                        <span>
                                            {promo.currentUses}/{promo.maxUses || '∞'} used
                                        </span>
                                    </div>

                                    <div className="promo-detail">
                                        <FaCalendarAlt />
                                        <span>{formatDate(promo.expiryDate)}</span>
                                    </div>

                                    {promo.minOrderAmount > 0 && (
                                        <div className="promo-detail">
                                            <FaShoppingCart />
                                            <span>Min: {promo.minOrderAmount} EGP</span>
                                        </div>
                                    )}
                                </div>

                                <div className="promo-card-actions">
                                    <button
                                        className={`toggle-promo-btn ${promo.isActive ? 'active' : ''}`}
                                        onClick={() => handleTogglePromo(promo.id, promo.isActive)}
                                    >
                                        {promo.isActive ? <FaToggleOn /> : <FaToggleOff />}
                                        {promo.isActive ? 'Active' : 'Inactive'}
                                    </button>
                                    <button
                                        className="delete-promo-btn"
                                        onClick={() => handleDeletePromo(promo.id, promo.code)}
                                        disabled={isComponentLoading('deletePromo')}
                                    >
                                        {isComponentLoading('deletePromo') ? (
                                            <Loading size="small" color="light" showText={false} />
                                        ) : (
                                            <FaTrash />
                                        )}
                                    </button>
                                </div>
                            </motion.div>
                        ))}
                    </AnimatePresence>
                )}
            </div>

            {/* Custom Alert */}
            <CustomAlert
                show={alertState.show}
                message={alertState.message}
                type={alertState.type}
                onConfirm={alertState.onConfirm}
                onCancel={alertState.onCancel}
                confirmText={alertState.confirmText}
                cancelText={alertState.cancelText}
            />
        </motion.div>
    );
}

export default PromoCodeManager;
